import { Component, OnInit, ChangeDetectorRef, EventEmitter, Output, inject } from '@angular/core';
import { BaseWidgetComponent } from '../base-widget/base-widget.component';
import { FeatureService, Feature, AvailableFeature } from 'src/app/services/feature.service';
import { DatasetsService } from 'src/app/services/datasets.service';
import { Meta } from 'src/app/models/meta';
import { WidgetService } from 'src/app/services/widget.service';
import { DialogService } from '../../../services/dialog.service';

interface FeatureCard extends Feature {
  scriptName?: string;
  paramsSummary?: string;
  tags?: string[];
  meta?: Meta;
  isExpanded?: boolean;
  isLoading?: boolean;
  testOutput?: {
    data?: any[];
    columns?: string[];
    error?: string;
    timestamp?: string;
    canShowChart?: boolean;
  };
  testDataset?: string;
  testIndex?: number;
}

type SortOption = 'alpha' | 'recent';

@Component({
  selector: 'app-feature-editor-widget',
  templateUrl: './feature-editor-widget.component.html',
  styleUrls: ['../base-widget/base-widget.component.scss', './feature-editor-widget.component.scss'],
  standalone: false
})
export class FeatureEditorWidgetComponent extends BaseWidgetComponent implements OnInit {
  private dialogs = inject(DialogService);
  @Output() onEditFile = new EventEmitter<{ parentId: string; path: string }>();

  public features: FeatureCard[] = [];
  public filteredFeatures: FeatureCard[] = [];
  public availableScripts: AvailableFeature[] = [];
  public datasets: string[] = [];
  public allTags: string[] = [];

  public isLoading = true;
  public searchFilter = '';
  public sortOption: SortOption = 'alpha';
  public selectedFeature: FeatureCard | null = null;

  // New feature creation
  public isCreatingNew = false;
  public newFeature: Partial<FeatureCard> = {};
  public selectedScript: AvailableFeature | null = null;

  // Editing state
  public editingNameId: string | null = null;

  constructor(
    private featureService: FeatureService,
    private datasetsService: DatasetsService,
    private widgetService: WidgetService,
    private cdr: ChangeDetectorRef
  ) {
    super();
  }

  async ngOnInit(): Promise<void> {
    await this.loadData();
  }

  private async loadData(): Promise<void> {
    this.isLoading = true;

    try {
      const [features, scripts, datasets] = await Promise.all([
        this.featureService.getAllAsync(this.teamName),
        this.featureService.getAvailableAsync(this.teamName),
        this.datasetsService.loadAsync(this.teamName)
      ]);

      this.availableScripts = scripts;
      this.datasets = datasets?.sort((a, b) => a.localeCompare(b)) || [];

      // Transform features into cards
      this.features = features.map(f => this.toFeatureCard(f));
      this.extractAllTags();
      this.applyFilters();
    } catch (error) {
      console.error('Error loading feature data:', error);
    } finally {
      this.isLoading = false;
    }
  }

  private toFeatureCard(feature: Feature): FeatureCard {
    const scriptName = feature.path?.split('/').pop() || 'unknown';
    const paramsSummary = this.buildParamsSummary(feature);
    const tags = (feature as any).tags || [];

    return {
      ...feature,
      scriptName,
      paramsSummary,
      tags,
      isExpanded: false,
      testDataset: this.datasets[0] || '',
      testIndex: Math.floor(Math.random() * 100000) + 1000
    };
  }

  private buildParamsSummary(feature: Feature): string {
    const exclude = ['name', 'path', 'isScript', 'deleted', 'group', 'tags', 'mutable'];
    const params = Object.entries(feature)
      .filter(([key]) => !exclude.includes(key))
      .map(([key, value]) => `${key}:${value}`)
      .slice(0, 4); // Show first 4 params

    return params.join(' ');
  }

  private extractAllTags(): void {
    const tagSet = new Set<string>();
    this.features.forEach(f => {
      f.tags?.forEach(t => tagSet.add(t));
    });
    this.allTags = Array.from(tagSet).sort();
  }

  public applyFilters(): void {
    let result = [...this.features];

    // Apply search filter
    if (this.searchFilter.trim()) {
      const search = this.searchFilter.toLowerCase();
      result = result.filter(f =>
        f.name.toLowerCase().includes(search) ||
        f.scriptName?.toLowerCase().includes(search) ||
        f.tags?.some(t => t.toLowerCase().includes(search))
      );
    }

    // Apply sort
    if (this.sortOption === 'alpha') {
      result.sort((a, b) => a.name.localeCompare(b.name));
    }
    // 'recent' would require a timestamp field on features

    this.filteredFeatures = result;
  }

  public onSearchChange(value: string): void {
    this.searchFilter = value;
    this.applyFilters();
  }

  public onSortChange(option: SortOption): void {
    this.sortOption = option;
    this.applyFilters();
  }

  public async selectFeature(feature: FeatureCard): Promise<void> {
    this.isCreatingNew = false;
    this.selectedFeature = feature;

    // Load meta if not loaded
    if (!feature.meta) {
      await this.loadFeatureMeta(feature);
    }
    this.cdr.detectChanges();
  }

  private async loadFeatureMeta(feature: FeatureCard): Promise<void> {
    feature.isLoading = true;
    try {
      feature.meta = await this.featureService.reflectAsync(this.teamName, feature.path);
    } catch (error) {
      console.error('Error loading feature meta:', error);
    } finally {
      feature.isLoading = false;
    }
  }

  public startEditName(feature: FeatureCard): void {
    this.editingNameId = feature.name;
  }

  public async saveName(feature: FeatureCard, newName: string): Promise<void> {
    if (newName === feature.name || !newName.trim()) {
      this.editingNameId = null;
      return;
    }

    try {
      const result: any = await this.featureService.renameAsync(this.teamName, feature.name, newName);
      if (result.status === 'error') {
        console.error('Rename failed:', result.message);
      } else {
        feature.name = newName;
      }
    } catch (error) {
      console.error('Error renaming feature:', error);
    } finally {
      this.editingNameId = null;
    }
  }

  public cancelEditName(): void {
    this.editingNameId = null;
  }

  public async deleteFeature(feature: FeatureCard): Promise<void> {
    if (!await this.dialogs.confirm(`Delete feature "${feature.name}"?`)) return;

    try {
      await this.featureService.deleteAsync(this.teamName, feature.name);
      this.features = this.features.filter(f => f.name !== feature.name);
      this.applyFilters();
      if (this.selectedFeature?.name === feature.name) {
        this.selectedFeature = null;
      }
    } catch (error) {
      console.error('Error deleting feature:', error);
    }
  }

  public async archiveFeature(feature: FeatureCard): Promise<void> {
    if (!await this.dialogs.confirm(`Archive feature "${feature.name}"?`)) return;

    try {
      // Archive uses the same delete endpoint - features are soft-deleted
      await this.featureService.deleteAsync(this.teamName, feature.name);
      this.features = this.features.filter(f => f.name !== feature.name);
      this.applyFilters();
      if (this.selectedFeature?.name === feature.name) {
        this.selectedFeature = null;
      }
    } catch (error) {
      console.error('Error archiving feature:', error);
    }
  }

  public async cloneFeature(feature: FeatureCard): Promise<void> {
    const baseName = feature.name.replace(/_copy\d*$/, '');
    let newName = `${baseName}_copy`;
    let counter = 1;

    while (this.features.some(f => f.name === newName)) {
      newName = `${baseName}_copy${counter++}`;
    }

    const newFeature = { ...feature };
    delete (newFeature as any).isExpanded;
    delete (newFeature as any).meta;
    delete (newFeature as any).testOutput;
    delete (newFeature as any).isLoading;
    newFeature.name = newName;

    try {
      await this.featureService.addAsync(this.teamName, newFeature);
      await this.loadData();
    } catch (error) {
      console.error('Error cloning feature:', error);
    }
  }

  public editCode(feature: FeatureCard): void {
    this.onEditFile.emit({ parentId: this.id, path: feature.path });
  }

  public async calculate(feature: FeatureCard): Promise<void> {
    if (!feature.testDataset || feature.testIndex === undefined) return;

    feature.isLoading = true;
    feature.testOutput = undefined;

    try {
      // Build params from feature
      const params = this.extractParams(feature);

      const result: any = await this.datasetsService.demo(
        this.teamName,
        feature.path,
        feature.testDataset,
        feature.testIndex,
        params
      );

      if (!result || !result.data || result.data.length === 0) {
        feature.testOutput = { error: 'No data returned', data: [], columns: [] };
      } else {
        const data = result.data;
        const is2D = Array.isArray(data[0]);
        const columns = feature.meta?.returns || [];

        // Check if OHLCV data
        const ohlcvColumns = ['open', 'high', 'low', 'close'];
        const canShowChart = ohlcvColumns.every(c => columns.includes(c));

        if (is2D) {
          feature.testOutput = {
            data: data.map((row: any[], i: number) => ({
              '#': i + 1,
              ...Object.fromEntries(row.map((v, j) => [columns[j] || `col${j}`, v]))
            })),
            columns: ['#', ...columns],
            timestamp: result.timestamp ? new Date(result.timestamp / 1000000).toLocaleString() : undefined,
            canShowChart
          };
        } else {
          feature.testOutput = {
            data: [{ '#': 1, ...Object.fromEntries(data.map((v: any, j: number) => [columns[j] || `col${j}`, v])) }],
            columns: ['#', ...columns],
            timestamp: result.timestamp ? new Date(result.timestamp / 1000000).toLocaleString() : undefined,
            canShowChart
          };
        }
      }
    } catch (error) {
      console.error('Error calculating feature:', error);
      feature.testOutput = { error: 'Calculation failed', data: [], columns: [] };
    } finally {
      feature.isLoading = false;
      this.cdr.detectChanges();
    }
  }

  private extractParams(feature: FeatureCard): Record<string, any> {
    const exclude = ['name', 'path', 'isScript', 'deleted', 'group', 'tags', 'mutable',
      'scriptName', 'paramsSummary', 'meta', 'isExpanded', 'isLoading', 'testOutput', 'testDataset', 'testIndex'];
    const params = Object.fromEntries(
      Object.entries(feature).filter(([key]) => !exclude.includes(key))
    );

    // Convert string values to numbers based on param type metadata
    if (feature.meta?.params) {
      for (const param of feature.meta.params) {
        if (param.type === 'number' && typeof params[param.name] === 'string') {
          const num = parseFloat(params[param.name]);
          if (!isNaN(num)) {
            params[param.name] = num;
          }
        }
      }
    }

    return params;
  }

  public randomIndex(feature: FeatureCard): void {
    // Generate a random index (this would ideally know the dataset size)
    feature.testIndex = Math.floor(Math.random() * 1000000);
    this.cdr.detectChanges();
  }

  // New feature creation
  public startNewFeature(): void {
    this.isCreatingNew = true;
    this.selectedScript = null;
    this.newFeature = {};
  }

  public selectScript(script: AvailableFeature): void {
    this.selectedScript = script;
    this.newFeature = {
      name: '',
      path: script.path,
      tags: []
    };
  }

  public cancelNewFeature(): void {
    this.isCreatingNew = false;
    this.selectedScript = null;
    this.newFeature = {};
  }

  public async saveNewFeature(): Promise<void> {
    if (!this.newFeature.name || !this.selectedScript) return;

    const newFeatureName = this.newFeature.name;

    try {
      const featureToSave = {
        ...this.newFeature,
        path: this.selectedScript.path
      };
      delete (featureToSave as any).tags; // Tags handled separately if needed

      await this.featureService.addAsync(this.teamName, featureToSave);
      await this.loadData();
      this.cancelNewFeature();

      // Select the newly created feature
      const newFeature = this.features.find(f => f.name === newFeatureName);
      if (newFeature) {
        await this.selectFeature(newFeature);
      }
    } catch (error) {
      console.error('Error saving new feature:', error);
    }
  }

  // Tag management
  public addTag(feature: FeatureCard, tag: string): void {
    if (!tag.trim() || feature.tags?.includes(tag)) return;
    if (!feature.tags) feature.tags = [];
    feature.tags.push(tag.trim());
    this.extractAllTags();
    // TODO: Persist tag to backend when API supports it
  }

  public removeTag(feature: FeatureCard, tag: string): void {
    if (!feature.tags) return;
    feature.tags = feature.tags.filter(t => t !== tag);
    this.extractAllTags();
    // TODO: Persist tag removal to backend when API supports it
  }

  public onParamChange(feature: FeatureCard, paramName: string, value: any): void {
    (feature as any)[paramName] = value;
    feature.paramsSummary = this.buildParamsSummary(feature);
  }
}
